<template>
  <div id="app">
    <!-- Initializing screen -->
    <AppInitializing v-if="!isAppReady" :stage="initStage" show-progress />

    <!-- Main content - Only shown when app is ready -->
    <template v-else>
      <router-view />
      <InvitationsBanner />
    </template>

    <!-- Migrazione da installazione precedente (jStorage) -->
    <AeModal
      v-model="showJStorageMigration"
      title="Benvenuto in Poderi2"
      size="medium"
    >
      <div class="jstorage-migration-content">
        <p>Si prega di fare login per caricare tutti i dati.</p>

        <AeAlert
          v-if="jStorageUnsyncedRecords.length > 0"
          type="warning"
          title="Dati non sincronizzati trovati"
        >
          Sono stati trovati <strong>{{ jStorageUnsyncedRecords.length }}</strong>
          record non sincronizzati dall'installazione precedente.
          Verranno inviati al server prima di procedere.
        </AeAlert>

        <AeAlert
          v-else
          type="info"
          title="Nessun dato da migrare"
        >
          Non ci sono record non sincronizzati dall'installazione precedente.
        </AeAlert>
      </div>

      <template #footer>
        <div class="jstorage-migration-actions">
          <AeButton
            v-if="!jStorageSent"
            variant="primary"
            :loading="jStorageSending"
            @click="sendLegacyData"
          >
            {{ jStorageUnsyncedRecords.length > 0 ? 'Invia dati e continua' : 'Continua' }}
          </AeButton>

          <AeButton
            v-if="jStorageSent"
            variant="success"
            @click="validateAndClear"
          >
            Valida Poderi2
          </AeButton>
        </div>
      </template>
    </AeModal>

    <!-- Errore di sincronizzazione (es. 401 su all_data) -->
    <div v-if="dbStore.syncError" class="sync-error-overlay" @click.self="dbStore.syncError = null">
      <div class="sync-error-dialog">
        <AeAlert type="error" title="Errore di sincronizzazione">
          {{ dbStore.syncError.message }}
        </AeAlert>
        <button class="sync-error-close" @click="dbStore.syncError = null">Chiudi</button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useAppStore } from './stores/app.js'
import { useDbStore } from './stores/dbStore.js'
import { useSkinStore } from './stores/skinStore.js'
import { useChatStore } from './stores/chatStore.js'
import { authService } from './services/auth.js'
import { fetchAllData, api } from './services/api.js'
import { initSwipeBack } from './services/swipeBack.js'
import AppInitializing from './components/AppInitializing.vue'
import InvitationsBanner from './components/InvitationsBanner.vue'
import { AeModal, AeButton, AeAlert } from '@aevue/components'

const router = useRouter()
const appStore = useAppStore()
const dbStore = useDbStore()
const skinStore = useSkinStore()
const chatStore = useChatStore()

// Inizializza la skin in base all'hostname (sincrono, prima del mount)
skinStore.init()

// --- jStorage migration ---
const showJStorageMigration = ref(false)
const jStorageUnsyncedRecords = ref([])
const jStorageMeta = ref({ id_device: null, user: null })
const jStorageSending = ref(false)
const jStorageSent = ref(false)

/**
 * Controlla se esiste jStorage in localStorage (dati da installazione precedente).
 * Scansiona fairshare alla ricerca di record con sync === false.
 */
function checkJStorage() {
  const raw = localStorage.getItem('jStorage')
  if (!raw) return

  let data
  try {
    data = JSON.parse(raw)
  } catch (e) {
    console.warn('App: jStorage malformato, ignorato')
    return
  }

  const fairshare = data?.fairshare
  if (!fairshare || typeof fairshare !== 'object') {
    // jStorage trovato ma senza chiave fairshare — non è un'installazione precedente, ignora
    return
  }

  const unsynced = []
  for (const [tableName, value] of Object.entries(fairshare)) {
    if (Array.isArray(value)) {
      for (const record of value) {
        if (record && record.sync === false) {
          unsynced.push({ table: tableName, record })
        }
      }
    }
  }

  jStorageUnsyncedRecords.value = unsynced
  jStorageMeta.value = {
    id_device: fairshare.id_device ?? null,
    user: fairshare.user ?? null,
  }
  showJStorageMigration.value = true
}

/**
 * Stub: invia i record non sincronizzati al server.
 */
async function sendLegacyData() {
  jStorageSending.value = true
  try {
    // STUB — endpoint da implementare lato backend
    if (jStorageUnsyncedRecords.value.length > 0) {
      await api.post('/api/v2/poderi2/migrate_data', {
        records: jStorageUnsyncedRecords.value,
        id_device: jStorageMeta.value.id_device,
        user: jStorageMeta.value.user,
      })
    }
  } catch (e) {
    console.warn('App: invio dati legacy fallito (stub):', e)
  } finally {
    jStorageSending.value = false
    jStorageSent.value = true
  }
}

/**
 * Rimuove jStorage dal localStorage e reindirizza al login.
 */
function validateAndClear() {
  try {
    const raw = localStorage.getItem('jStorage')
    if (raw) {
      const data = JSON.parse(raw)
      delete data.fairshare
      if (data.__jstorage_meta?.CRC32) {
        delete data.__jstorage_meta.CRC32.fairshare
      }
      localStorage.setItem('jStorage', JSON.stringify(data))
    }
  } catch (e) {
    console.warn('App: errore pulizia fairshare da jStorage:', e)
  }
  showJStorageMigration.value = false
  appStore.login()
}

// true solo quando fetchServerConfig fallisce E non c'è nessuna cache disponibile
const configFailed = ref(false)

const isLoading = computed(() => appStore.isLoading)
const isDbReady = computed(() => dbStore.isReady)
const isAppReady = computed(() => !configFailed.value && !isLoading.value && isDbReady.value)

const initStage = computed(() => {
  if (configFailed.value) return 'error'
  if (isLoading.value) return 'config'
  if (!isDbReady.value) return 'database'
  return 'ready'
})

// Auto-recovery: se il DB è pronto ma ha bisogno di re-sync
// (sync precedente interrotto, DB corrotto, ecc.)
// Tenta il recovery automatico max 1 volta per sessione.
let recoveryAttempted = false
watch(() => dbStore.needsResync, async (needs) => {
  if (!needs || recoveryAttempted) return

  // Serve utente autenticato E schema con tabelle per poter fare recovery
  if (!appStore.isAuthenticated) {
    console.log('App: DB needs resync ma utente non autenticato, skip')
    return
  }

  const config = appStore.dexieConfig
  const profileId = appStore.user?.uid
  if (!profileId || !config || Object.keys(config).length === 0) {
    console.log('App: DB needs resync ma config/profilo non disponibili, skip')
    return
  }

  recoveryAttempted = true
  console.warn('App: DB needs resync, attempting recovery...')

  const ok = await dbStore.attemptRecovery(config, fetchAllData, profileId)
  if (ok) {
    console.log('App: DB recovery completato')
  } else {
    console.error('App: DB recovery fallito — serve intervento manuale')
  }
})

// Handler per sincronizzazione forzata prima di chiudere la pagina
const handleBeforeUnload = async (e) => {
  // Forza la sincronizzazione immediata
  await dbStore.syncToServer(true)
}

function handleVisibilityChange() {
  if (document.visibilityState === 'visible') appStore.checkForNewFarmCenters()
}

// Cleanup dello swipe back gesture
let cleanupSwipeBack = null

// Cleanup push notification listeners
let removeNotificationListeners = null

// Imposta il tema glass su document.documentElement
document.documentElement.setAttribute('data-theme', 'glass')

// Carica la configurazione del server all'avvio
onMounted(async () => {

  // Gestione deep link su nativo: intercetta appUrlOpen.
  // - Custom scheme:   poderi2://auth/callback?code=xxx  → /auth/callback?code=xxx
  // - Universal Link:  https://studio.poderi.app/pagina  → /pagina
  if (window?.Capacitor?.isNativePlatform?.() && window?.Capacitor?.Plugins?.App) {
    window.Capacitor.Plugins.App.addListener('appUrlOpen', async (data) => {
      try {
        const url = new URL(data.url)
        let path

        if (url.protocol === 'https:' || url.protocol === 'http:') {
          // Universal Link (https://studio.poderi.app/...)
          // Se l'URL usa hash routing (#/percorso), estrai il percorso dal hash
          if (url.hash && url.hash.startsWith('#/')) {
            path = url.hash.slice(1) // '#/join/abc' → '/join/abc'
          } else {
            path = url.pathname + url.search
          }
        } else {
          // Custom scheme (poderi2://auth/callback?...) → host è il primo segmento del path
          path = '/' + url.host + url.pathname + url.search
        }

        console.log('App: appUrlOpen → navigating to:', path)

        // Chiudi il browser OAuth2 (SFSafariViewController/Chrome Custom Tab)
        // prima di navigare alla route di callback
        if (url.host === 'auth' && url.pathname === '/callback') {
          try {
            const { Browser } = await import(/* @vite-ignore */ '@capacitor/browser')
            await Browser.close()
            console.log('App: Browser OAuth2 chiuso')
          } catch (e) {
            console.warn('App: errore chiusura Browser:', e)
          }
        }

        router.push(path)
      } catch (e) {
        console.error('App: errore parsing appUrlOpen URL:', e, data.url)
      }
    })
  }

  // Foreground check: sync solo se ci sono nuove farm_center.
  // Nativo: appStateChange; Web: visibilitychange.
  if (window?.Capacitor?.isNativePlatform?.() && window?.Capacitor?.Plugins?.App) {
    window.Capacitor.Plugins.App.addListener('appStateChange', ({ isActive }) => {
      if (isActive) appStore.checkForNewFarmCenters()
    })
  } else {
    document.addEventListener('visibilitychange', handleVisibilityChange)
  }

  try {
    // Gestione redirect OAuth2 da URL senza hash a hash router
    const currentPath = window.location.pathname
    const currentSearch = window.location.search


    
    
    if (currentPath === '/auth/callback' && currentSearch.includes('code=')) {
      console.log('🔄 Redirect OAuth2 da URL senza hash a hash router')
      console.log('Current URL:', window.location.href)
      
      // Estrai i parametri
      const urlParams = new URLSearchParams(currentSearch)
      const code = urlParams.get('code')
      const state = urlParams.get('state')
      const error = urlParams.get('error')
      
      // Costruisci l'URL con hash router
      let hashPath = '/auth/callback'
      if (code || state || error) {
        const hashParams = new URLSearchParams()
        if (code) hashParams.set('code', code)
        if (state) hashParams.set('state', state)
        if (error) hashParams.set('error', error)
        hashPath += '?' + hashParams.toString()
      }
      
      console.log('🔄 Redirect to hash path:', hashPath)

      // Pulisce il pathname del browser: /auth/callback -> /
      // Altrimenti resta visibile /auth/callback#/ nella barra indirizzi.
      // replaceState non ricarica la pagina, cambia solo l'URL visualizzato.
      window.history.replaceState({}, document.title, '/#' + hashPath)

      // Naviga alla rotta hash /auth/callback (monta AuthCallback.vue)
      router.replace(hashPath)
    }



    try {
      await appStore.fetchServerConfig()
      if (appStore.isOffline) {
        console.warn('App avviata in modalità offline — serverConfig da cache locale')
      } else {
        console.log('App initialized with server config')
      }
    } catch (error) {
      // fetchServerConfig ha fallito E non c'è nessuna cache: errore definitivo
      console.error('Impossibile caricare la configurazione del server:', error.message)
      configFailed.value = true
      // Inizializza il DB con config vuota per sbloccare isDbReady e uscire dallo spinner
      await dbStore.init({})
      return
    }

    //fa l'init del db
    await dbStore.init(appStore.dexieConfig);
    console.log('Database initialized')

    // Controlla se esiste una installazione precedente (jStorage)
    checkJStorage()

    // Tenta di ripristinare la sessione utente se esistente
    await authService.restoreSession()

    // Se l'utente è già autenticato, connetti lo stream SSE e avvia sync watchers
    if (appStore.isAuthenticated) {
      dbStore.connectStream()
      dbStore.startSyncWatchers({
        config: appStore.dexieConfig,
        fetchDataFn: async () => await fetchAllData()
      })
      appStore.registerPushToken()
    }

    // Aggiungi listener per sincronizzazione forzata
    window.addEventListener('beforeunload', handleBeforeUnload)

    // Inizializza swipe-back gesture per iOS (solo su mobile)
    cleanupSwipeBack = initSwipeBack(router)

    // Avvia listener push notifications (foreground + tap)
    // Fire-and-forget: startListening è no-op su web
    import('./composables/useNotifications.js').then(({ startListening }) => {
      startListening(
        // foreground: arriva mentre l'app è aperta
        (n) => {
          if (n.data?.type === 'chat') {
            // Messaggio chat: aggiorna unread count in sidebar
            chatStore.loadRooms()
            // Se la room è già aperta, aggiorna anche i messaggi
            if (chatStore.currentRoomId === n.data?.id_room) {
              chatStore.loadMessages()
            }
          } else {
            appStore.addNotification(n)
          }
        },
        // tap: utente tocca la notifica (background/closed)
        (n) => {
          if (n.data?.type === 'chat') {
            chatStore.loadRooms()
            if (n.data?.route) router.push(n.data.route)
          } else {
            appStore.addNotification(n)
            if (n.data?.route) router.push(n.data.route)
          }
        }
      ).then(cleanup => { removeNotificationListeners = cleanup })
    })
  } catch (error) {
    console.error('Errore inatteso durante inizializzazione:', error)
  }
})

onUnmounted(() => {
  // Rimuovi listener quando il componente viene smontato
  window.removeEventListener('beforeunload', handleBeforeUnload)
  document.removeEventListener('visibilitychange', handleVisibilityChange)

  // Cleanup swipe back gesture
  if (cleanupSwipeBack) {
    cleanupSwipeBack()
  }

  // Cleanup push notification listeners
  if (removeNotificationListeners) {
    removeNotificationListeners()
  }
})
</script>

<style>

body, html {
  margin: 0;
  padding: 0;
}

#app {
  min-height: 100vh;
}

.router-link-active {
  font-weight: bold;
}

.text-extreme-danger {
  color: #b235dc !important;
  font-weight: bold;
}

.jstorage-migration-content {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.jstorage-migration-content p {
  margin: 0;
  font-size: 1rem;
  color: var(--text-primary, #2d3748);
}

.jstorage-migration-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 1rem;
}

.sync-error-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 1.5rem;
}

.sync-error-dialog {
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  max-width: 400px;
  width: 100%;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
}

.sync-error-close {
  margin-top: 1rem;
  width: 100%;
  padding: 0.6rem;
  background: #ef4444;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 1rem;
  cursor: pointer;
}
</style>
